import React, { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { ReactComponent as DocumentActivityIcon } from '../../../shared/icons/DocumentActivity.svg';
import { ReactComponent as ClauseTemplateIcon } from '../../../shared/icons/icon-clauses.svg';
import { ReactComponent as DocumentTeamIcon } from '../../../shared/icons/users-group-people.svg';
import { ReactComponent as CloseIcon } from '../../../shared/icons/close-icon.svg';
import { ReactComponent as PlaceholdersIcon } from '../../../shared/icons/placeholders-icon.svg';
import { ReactComponent as AttachmentIcon } from '../../../shared/icons/attachment-icon.svg';
import { DocumentPermissions } from '../../../pages/document-wizard/shared/hooks/use-document-team-permissions/use-document-team-permissions';
import { ClauseTemplatesSidebar } from '../document-editor/components/clauseTemplatesSidebar';
import { DocumentActivitySidebar } from '../document-editor/components/documentActivitySidebar';
import styles from './styles.module.scss';
import { DocumentTeamSidebar } from '../../document-sidebars/document-team-sidebar';
import Tooltip from '../../tooltip';
import PendingReviewWarningModal from '../document-wizard-header/modals/PendingReviewWarningModal';
import SendSlideout from '../document-wizard-header/components/headerActions/send/SendSlideout';
import { useDeal } from '../../../contexts/DealContext';
import { DocumentSourceEnum, ShareWithUnresolvedSuggestionsWarning } from '../document-editor/constants/EditorConstats';
import { useDocumentState } from '../../../contexts/DocumentContext';
import { CounterpartyPermissionLevel } from '../../../types/counterparty-permissions';
import { useNavigation } from '../../../contexts/NavigationContext';
import { useDocumentRole } from '../../../shared/hooks/useDocumentRole/useDocumentRole';
import { useModals } from '../../../contexts/ModalsContext';
import { useQuery } from '@apollo/client';
import { queriesV2 } from '../../../api';
import PlaceholdersSidebarContainer from '../../document-sidebars/placeholders-sidebar/PlaceholdersSidebarContainer';
import DocumentAttachmentsSidebarContainer from '../document-editor/components/documentAttachmentsSidebar/documentAttachmentsSidebarContainer';
import { usePendingCounterpartySuggestions } from '../document-wizard-header/components/headerActions/usePendingCounterpartySuggestions';

type DocumentActivitiesAndTemplatesNavigationProps = {
  isDocumentCreator: boolean;
  isDocumentRestricted: boolean;
  isCollapsible: boolean;
  permissions: DocumentPermissions;
  onClose: () => void;
  handleSendDocumentMutation: () => Promise<void>;
  canEdit: boolean;
  isReviewing: boolean;
  hasInviteSendingPermission: boolean;
  dealId: string;
  teamId: string;
  documentId: string;
  optionalMessageInputRef: RefObject<HTMLTextAreaElement>;
  onSave: (() => Promise<void>) | undefined;
  canShare: boolean;
  isShared: boolean | undefined;
  isDraft: boolean;
  isUploaded: boolean;
};

enum ActiveSidenavPanel {
  CLAUSES = 'clauses',
  ACTIVITY = 'activity',
  TEAM = 'team',
  PLACEHOLDERS = 'placeholders',
  ATTACHMENTS = 'attachments',
}

const DocumentActivitiesAndTemplatesSidenav = ({
  permissions,
  isDocumentCreator,
  isDocumentRestricted,
  isCollapsible,
  onClose,
  handleSendDocumentMutation,
  canEdit,
  isReviewing,
  hasInviteSendingPermission,
  dealId,
  teamId,
  documentId,
  optionalMessageInputRef,
  onSave,
  canShare,
  isDraft,
  isUploaded,
  isShared,
}: DocumentActivitiesAndTemplatesNavigationProps): JSX.Element => {
  const { closePendingReviewWarningModal: isPendingReviewWarningModalClosedExternally, setShowSendDocumentModal } = useModals();
  const {
    sidenavPlaceholdersCollapsed: isSidenavPlaceholdersCollapsed,
    manageDealParticipantsCollapsed: isManageDealParticipantsCollapsed,
    toggleSidenavPlaceholdersCollapsed,
    toggleManageDealParticipantsCollapsed,
  } = useNavigation();
  const [activeSidenavPanel, setActiveSidenavPanel] = useState<ActiveSidenavPanel | null>(null);
  const [shouldShowToggleButton, setShouldShowToggleButton] = useState(false);

  const [showWarningReviewModal, setShowWarningReviewModal] = useState(isPendingReviewWarningModalClosedExternally);

  const [showSendToCounterpartyModal, setShowSendToCounterpartyModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Get current permission level from context
  const { state: { counterpartyPermissionLevel: reduxCounterpartyPermissionLevel, isEditorLoaded: isEditorLoadedState, source: documentSource } } = useDocumentState();

  // Local state for permission dropdown - initialized from Redux (current document state)
  const [localCounterpartyPermissionLevel, setLocalCounterpartyPermissionLevel] = useState<CounterpartyPermissionLevel>(reduxCounterpartyPermissionLevel);

  const { hasCurrentTeamPossession } = useDocumentRole(documentId);

  // "Before You Go… are you ready to send the document for review or signing?"
  // presupposes you HOLD the document. Without the possession check, a
  // counterparty who had already sent it back was prompted to send it again
  // every time they closed the read-only view. See DocumentWizardHeader, which
  // carries the same guard for its own copy of this modal.
  const shouldWarnBeforeLeaving =
    isReviewing && hasInviteSendingPermission && !isDocumentCreator && !!hasCurrentTeamPossession;

  const shouldTemplatesOptionBeRendered = isDocumentCreator && !isDocumentRestricted;
  const subNavRef = useRef(null);
  const { currentDeal } = useDeal();
  const { userRole } = useDocumentRole(documentId);
  const pendingCounterpartySuggestions = usePendingCounterpartySuggestions(documentId, userRole);
  const isDealShared = !!currentDeal?.receiverEmail;
  const hasEditPermission = !!permissions?.hasEditPermission;
  const isEditorLoaded = documentSource === DocumentSourceEnum.APP ? isEditorLoadedState : true;

  const { data: dealTeamsData } = useQuery(queriesV2.getDealTeamsByTeamId, {
    variables: {
      dealId,
    },
  });

  // Query kept for potential side effects (cache population)
  useQuery(queriesV2.getDocumentExtraAttachments, {
    variables: { documentId: String(documentId) },
    skip: !documentId,
    fetchPolicy: 'network-only',
  });

  const setActiveSidenav = (activePanel: ActiveSidenavPanel | null) => {
    const newActivePanel = activePanel === activeSidenavPanel ? null : activePanel;

    setActiveSidenavPanel(newActivePanel);
  };

  const toggleCollapse = () => {
    toggleSidenavPlaceholdersCollapsed(true);
    setActiveSidenavPanel(null);
  };

  const handleSendModalHide = () => {
    setShowSendToCounterpartyModal(false);
    setShowSendDocumentModal(false);
  };

  useEffect(() => {
    if (isPendingReviewWarningModalClosedExternally === true && showWarningReviewModal) {
      setShowWarningReviewModal(false);
    }
  }, [isPendingReviewWarningModalClosedExternally, showWarningReviewModal]);

  // Use ref to track previous open state
  const prevShowSendToCounterpartyModalRef = useRef(showSendToCounterpartyModal);

  // Sync permission values from Redux ONLY when modal transitions from closed to open
  useEffect(() => {
    const wasOpenBefore = prevShowSendToCounterpartyModalRef.current;
    const isOpenNow = showSendToCounterpartyModal;

    // Only sync when transitioning from closed to open (not just when open)
    if (!wasOpenBefore && isOpenNow) {
      setLocalCounterpartyPermissionLevel(reduxCounterpartyPermissionLevel);
    }

    // Update ref for next render
    prevShowSendToCounterpartyModalRef.current = showSendToCounterpartyModal;
  }, [showSendToCounterpartyModal, reduxCounterpartyPermissionLevel]);

  const handleClose = () => {
    if (shouldWarnBeforeLeaving) {
      setShowWarningReviewModal(true);
    } else {
      onClose && onClose();
    }
  };

  const sendDocumentForReview = useCallback(async () => {
    setIsLoading(true);

    if (documentSource === DocumentSourceEnum.IMPORTED) {
      return handleSendDocumentMutation();
    }

    return handleSendDocumentMutation();
  }, [documentSource, handleSendDocumentMutation]);

  const getPendingSuggestionsAlert = () => {
    // From the relational `document_suggestions` rows — the same source the
    // server gates on. This used to read CKEditor's TrackChanges plugin, which
    // is always undefined now, so the warning silently never appeared.
    const suggestionsNumber = pendingCounterpartySuggestions.fromCounterparty;

    if (!suggestionsNumber) return null;
    return {
      text: ShareWithUnresolvedSuggestionsWarning.text.replace('{num}', `${suggestionsNumber}`),
      title: ShareWithUnresolvedSuggestionsWarning.title,
    };
  };

  const shouldShowInternalSendForSigning =
    isDocumentCreator &&
    hasEditPermission &&
    (isDraft || isReviewing || isUploaded || isShared) &&
    hasCurrentTeamPossession &&
    isEditorLoaded;
  const shouldShowDocumentActionsForReceiver = !isDocumentCreator && hasCurrentTeamPossession && isReviewing;

  const shouldShowSendForSigning = isEditorLoaded && shouldShowDocumentActionsForReceiver && canEdit;

  // eslint-disable-next-line unused-imports/no-unused-vars
  const [collapsePlaceholderSidenav, setCollapsePlaceholderSidenav] = useState(isSidenavPlaceholdersCollapsed);

  const handlePlaceholderSidenavClick = () => {
    setActiveSidenav(ActiveSidenavPanel.PLACEHOLDERS);

    toggleSidenavPlaceholdersCollapsed(collapsePlaceholderSidenav);
  };

  useEffect(() => {
    if (isSidenavPlaceholdersCollapsed) {
      setActiveSidenavPanel(null);
    } else {
      setActiveSidenavPanel(ActiveSidenavPanel.PLACEHOLDERS);
    }
  }, [isSidenavPlaceholdersCollapsed]);

  // Handle opening participants sidebar from external triggers (like modal buttons)
  useEffect(() => {
    if (!isManageDealParticipantsCollapsed) {
      setActiveSidenavPanel(ActiveSidenavPanel.TEAM);
      // Reset the state to avoid staying "open" in context
      toggleManageDealParticipantsCollapsed(true);
    }
  }, [isManageDealParticipantsCollapsed, toggleManageDealParticipantsCollapsed]);

  // Handle opening placeholders sidebar from balloon popup "Link field..." button
  useEffect(() => {
    const handleOpenPlaceholdersSidebar = () => {
      setActiveSidenavPanel(ActiveSidenavPanel.PLACEHOLDERS);
      toggleSidenavPlaceholdersCollapsed(false);
    };

    window.addEventListener('openPlaceholdersSidebar', handleOpenPlaceholdersSidebar);
    return () => {
      window.removeEventListener('openPlaceholdersSidebar', handleOpenPlaceholdersSidebar);
    };
  }, [toggleSidenavPlaceholdersCollapsed]);

  return (
    <div className={classNames(styles.subNavMenuWrapper)} ref={subNavRef}>
      <PendingReviewWarningModal
        showModal={showWarningReviewModal}
        hideModal={setShowWarningReviewModal}
        onCloseDocument={onClose}
        canEdit={canEdit}
        documentId={documentId}
        teamId={teamId}
        dealId={dealId}
        dealTeamsData={dealTeamsData}
        onSave={onSave}
        canShare={canShare}
        shouldShowInternalSendForSigning={shouldShowInternalSendForSigning}
        shouldShowSendForSigning={shouldShowSendForSigning}
      />
      <SendSlideout
        isOpen={showSendToCounterpartyModal}
        onClose={handleSendModalHide}
        isDealShared={isDealShared}
        handler={sendDocumentForReview}
        isLoading={isLoading}
        suggestionsAlert={getPendingSuggestionsAlert?.()}
        optionalMessageInputRef={optionalMessageInputRef}
        teamId={teamId}
        counterpartyPermissionLevel={localCounterpartyPermissionLevel}
        onCounterpartyPermissionChange={setLocalCounterpartyPermissionLevel}
        documentSource={documentSource}
      />
      <div className={styles.documentNavigationBar}>
        <button
          type="button"
          onClick={handleClose}
          className={styles.closeButton}
          data-testid="document-header__close-cta"
        >
          <Tooltip
            testId="sidebar-button-activity"
            placement="bottom"
            icon={<CloseIcon className={styles.closeIcon} />}
          >
            Exit
          </Tooltip>
        </button>
        <div
          className={classNames(styles.documentNavigationBar__icon, {
            [styles.active]: isCollapsible && activeSidenavPanel === ActiveSidenavPanel.ACTIVITY,
            [styles.enabled]: isCollapsible,
          })}
          onClick={() => setActiveSidenav(ActiveSidenavPanel.ACTIVITY)}
        >
          <Tooltip
            testId="sidebar-button-activity"
            placement="bottom"
            icon={<DocumentActivityIcon height="22" width="20" />}
          >
            <FormattedMessage id="nav.activity" defaultMessage="Activity" />
          </Tooltip>
        </div>
        {shouldTemplatesOptionBeRendered && (
          <div
            className={classNames(styles.documentNavigationBar__icon, {
              [styles.active]: isCollapsible && activeSidenavPanel === ActiveSidenavPanel.CLAUSES,
              [styles.enabled]: isCollapsible,
            })}
            onClick={() => setActiveSidenav(ActiveSidenavPanel.CLAUSES)}
          >
            <Tooltip
              testId="sidebar-button-clause"
              placement="bottom"
              icon={<ClauseTemplateIcon height="22" width="20" />}
            >
              <FormattedMessage id="nav.clauses" defaultMessage="Clauses" />
            </Tooltip>
          </div>
        )}
        <div
          className={classNames(styles.documentNavigationBar__icon, {
            [styles.active]: isCollapsible && activeSidenavPanel === ActiveSidenavPanel.TEAM,
            [styles.enabled]: isCollapsible,
          })}
          onClick={() => setActiveSidenav(ActiveSidenavPanel.TEAM)}
        >
          <Tooltip testId="sidebar-button-team" placement="bottom" icon={<DocumentTeamIcon height="22" width="20" />}>
            <FormattedMessage id="nav.team" defaultMessage="Participants" />
          </Tooltip>
        </div>
        <div
          className={classNames(styles.documentNavigationBar__icon, {
            [styles.active]: isCollapsible && activeSidenavPanel === ActiveSidenavPanel.PLACEHOLDERS,
            [styles.enabled]: isCollapsible,
          })}
          onClick={handlePlaceholderSidenavClick}
        >
          <Tooltip testId="sidebar-button-team" placement="bottom" icon={<PlaceholdersIcon height="22" width="20" />}>
            <FormattedMessage id="nav.placeholders" defaultMessage="Placeholders" />
          </Tooltip>
        </div>
        {
          <div
            className={classNames(styles.documentNavigationBar__icon, {
              [styles.active]: isCollapsible && activeSidenavPanel === ActiveSidenavPanel.ATTACHMENTS,
              [styles.enabled]: isCollapsible,
            })}
            onClick={() => setActiveSidenav(ActiveSidenavPanel.ATTACHMENTS)}
          >
            <Tooltip testId="sidebar-button-team" placement="bottom" icon={<AttachmentIcon height="22" width="20" />}>
              <FormattedMessage id="nav.attachments" defaultMessage="Attachments" />
            </Tooltip>
          </div>
        }
      </div>
      {isCollapsible && (
        <div
          id="document-wizard-side-nav"
          className={classNames(styles.subNav, { [styles.collapsed]: !activeSidenavPanel })}
          aria-expanded={!activeSidenavPanel}
          onMouseEnter={() => setShouldShowToggleButton(true)}
          onMouseLeave={() => setShouldShowToggleButton(false)}
        >
          <button
            className={classNames(styles.subNavToggle, { [styles.subNavToggle__hidden]: !shouldShowToggleButton })}
            type="button"
            aria-expanded={!activeSidenavPanel}
            aria-controls="main-sub-nav"
            onClick={toggleCollapse}
          />
          <div className={styles.subNavMask}>
            <div className={styles.subNavContent}>
              {shouldTemplatesOptionBeRendered && activeSidenavPanel === ActiveSidenavPanel.CLAUSES && (
                <ClauseTemplatesSidebar permissions={permissions} />
              )}
              {activeSidenavPanel === ActiveSidenavPanel.ACTIVITY && <DocumentActivitySidebar />}
              {activeSidenavPanel === ActiveSidenavPanel.TEAM && <DocumentTeamSidebar />}
              {activeSidenavPanel === ActiveSidenavPanel.PLACEHOLDERS && (
                <PlaceholdersSidebarContainer
                  documentId={documentId}
                  dealId={dealId}
                  teamId={teamId}
                  isReadOnly={!canEdit}
                  isCounterparty={!isDocumentCreator}
                />
              )}
              {activeSidenavPanel === ActiveSidenavPanel.ATTACHMENTS && (
                <DocumentAttachmentsSidebarContainer activeUuid={null} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentActivitiesAndTemplatesSidenav;
